import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import "../charts.scss";
import { primaryColor, toolTipBackground } from "../chartsStyleConstants";
import useLargeScreen from "../../../hooks/useLargeScreen";

export interface BarChartData {
  label: string;
  value: number;
}

interface BarChartProps {
  data: BarChartData[];
  color?: string;
  horizontal?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({ data, color = primaryColor, horizontal = false }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState({ width: 508, height: 235 });
  const isLargeScreen = useLargeScreen();

  useEffect(() => {
    const updateDimensions = () => {
      if (wrapperRef.current) {
        const { width, height } = wrapperRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };
    window.addEventListener("resize", updateDimensions);
    updateDimensions();
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  useEffect(() => {
    if (!svgRef.current) return;

    const { width, height } = dimensions;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Measure Y label width
    let maxLabelWidth = 0;
    if (!horizontal) {
      const yTemp = d3
        .scaleLinear()
        .domain([0, d3.max(data, (d) => d.value) || 1])
        .nice()
        .range([height - 40, 20]);

      const tempAxis = svg.append("g").call(
        d3
          .axisLeft(yTemp)
          .ticks(5)
          .tickFormat((d: any) => d3.format(".2s")(d).replace("G", "B"))
      );
      tempAxis.selectAll("text").each(function () {
        const w = (this as SVGTextElement).getBBox().width;
        if (w > maxLabelWidth) maxLabelWidth = w;
      });
      tempAxis.remove();
    } else {
      const yTemp = d3
        .scaleBand()
        .domain(data?.map((d) => d.label))
        .range([20, height - 40])
        .padding(0.2);

      const tempAxis = svg.append("g").call(d3.axisLeft(yTemp));
      tempAxis.selectAll("text").each(function () {
        const w = (this as SVGTextElement).getBBox().width;
        if (w > maxLabelWidth) maxLabelWidth = w;
      });
      tempAxis.remove();
    }

    const tickPadding = 6;
    const margin = {
      top: 20,
      right: 20,
      bottom: 40,
      left: maxLabelWidth + tickPadding + 8,
    };

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "chart-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", toolTipBackground)
      .style("color", "black")
      .style("padding", "5px")
      .style("border-radius", "4px")
      .style("font-size", "10px")
      .style("pointer-events", "none");

    // Vertical Bar Chart

    if (!horizontal) {
      const x = d3
        .scaleBand()
        .domain(data?.map((d) => d.label))
        .range([margin.left, width - margin.right])
        .padding(0.2);

      const y = d3
        .scaleLinear()
        .domain([0, d3.max(data, (d) => d.value) || 1])
        .nice()
        .range([height - margin.bottom, margin.top]);

      // Gridlines
      svg
        .append("g")
        .attr("class", "grid")
        .attr("transform", `translate(${margin.left},0)`)
        .call(
          d3
            .axisLeft(y)
            .ticks(5)
            .tickSize(-(width - margin.left - margin.right))
            .tickFormat(() => "")
        )
        .call((g) => g.select(".domain").remove())
        .selectAll("line")
        .style("stroke-dasharray", "4");

      // Bars
      svg
        .append("g")
        .attr("fill", color)
        .selectAll("rect")
        .data(data)
        .join("rect")
        .attr("x", (d) => x(d.label)!)
        .attr("y", (d) => y(d.value))
        .attr("height", (d) => y(0) - y(d.value))
        .attr("width", x.bandwidth())
        .attr("rx", 4)
        .attr("ry", 4)
        .on("mouseover", (event, d) => {
          tooltip
            .style("visibility", "visible")
            .text(`${d.label}: ${d3.format(".2s")(d.value).replace("G", "B")}`)
            .style("top", `${event.pageY + 10}px`)
            .style("left", `${event.pageX + 10}px`);
        })
        .on("mousemove", (event) => {
          tooltip.style("top", `${event.pageY + 10}px`).style("left", `${event.pageX + 10}px`);
        })
        .on("mouseout", () => tooltip.style("visibility", "hidden"));

      // Labels inside bars
      svg
        .append("g")
        .selectAll("text")
        .data(data)
        .join("text")
        .attr("x", (d) => x(d.label)! + x.bandwidth() / 2)
        .attr("y", (d) => y(d.value) + 15)
        .attr("text-anchor", "middle")
        .attr("fill", "white")
        .style("font-size", "8px")
        .text((d) => d3.format(".2s")(d.value).replace("G", "B"));

      // X Axis
      const xAxis = svg
        .append("g")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x).tickSizeOuter(0))
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll("line").remove());

      // Wrap X-axis labels if too wide
      xAxis.selectAll("text").each(function (d) {
        const self = d3.select(this);
        const text = String(d);
        const availableWidth = x.bandwidth();
        const temp = self.clone(true).attr("opacity", 0);
        const textWidth = (temp.node() as SVGTextElement).getComputedTextLength();
        temp.remove();

        if (textWidth > availableWidth) {
          self.text(null);
          const parts = text.includes("-")
            ? [text.split("-")[0] + "-", text.split("-")[1]]
            : [text.slice(0, Math.ceil(text.length / 2)), text.slice(Math.ceil(text.length / 2))];

          self.append("tspan").attr("x", 0).attr("dy", "0.8em").text(parts[0]);

          self.append("tspan").attr("x", 0).attr("dy", "1.2em").text(parts[1]);
        }
      });

      // Y-axis
      svg
        .append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .call(
          d3
            .axisLeft(y)
            .ticks(5)
            .tickFormat((d: any) => d3.format(".2s")(d).replace("G", "B"))
            .tickPadding(tickPadding)
        )
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll("line").remove());
    }

    // Horizontal Bar Chart
    else {
      const y = d3
        .scaleBand()
        .domain(data?.map((d) => d.label))
        .range([margin.top, height - margin.bottom])
        .padding(0.2);

      const x = d3
        .scaleLinear()
        .domain([0, d3.max(data, (d) => d.value) || 1])
        .nice()
        .range([margin.left, width - margin.right]);

      // Gridlines
      svg
        .append("g")
        .attr("class", "grid")
        .attr("transform", `translate(0,${margin.top})`)
        .call(
          d3
            .axisTop(x)
            .ticks(5)
            .tickSize(-(height - margin.top - margin.bottom))
            .tickFormat(() => "")
        )
        .call((g) => g.select(".domain").remove())
        .selectAll("line")
        .style("stroke-dasharray", "4 4");

      // Bars
      svg
        .append("g")
        .attr("fill", color)
        .selectAll("rect")
        .data(data)
        .join("rect")
        .attr("y", (d) => y(d.label)!)
        .attr("x", x(0))
        .attr("height", y.bandwidth())
        .attr("width", (d) => x(d.value) - x(0))
        .attr("rx", 4)
        .attr("ry", 4)
        .on("mouseover", (event, d) => {
          tooltip
            .style("visibility", "visible")
            .text(`${d.label}: ${d3.format(".2s")(d.value).replace("G", "B")}`)
            .style("top", `${event.pageY + 10}px`)
            .style("left", `${event.pageX + 10}px`);
        })
        .on("mousemove", (event) => {
          tooltip.style("top", `${event.pageY + 10}px`).style("left", `${event.pageX + 10}px`);
        })
        .on("mouseout", () => tooltip.style("visibility", "hidden"));

      // Value labels
      svg
        .append("g")
        .selectAll("text")
        .data(data)
        .join("text")
        .attr("y", (d) => y(d.label)! + y.bandwidth() / 2 + 5)
        .attr("x", (d) => x(d.value) - 5)
        .attr("text-anchor", "end")
        .attr("class", "inside-value")
        .text((d) => d3.format(".2s")(d.value).replace("G", "B"));

      // X Axis
      svg
        .append("g")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(
          d3
            .axisBottom(x)
            .ticks(5)
            .tickFormat((d: any) => d3.format(".2s")(d).replace("G", "B"))
        )
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll("line").remove());

      // Y-axis
      svg
        .append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .call(d3.axisLeft(y).tickPadding(tickPadding))
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll("line").remove());
    }

    return () => {
      tooltip.remove();
    };
  }, [data, dimensions, color, horizontal, isLargeScreen]);

  return (
    <div ref={wrapperRef} style={{ width: "100%", height: "100%" }}>
      <svg ref={svgRef} width="100%" height={dimensions.height} />
    </div>
  );
};

export default BarChart;
